import React, { useState } from 'react';
import { Card, Typography, Stack, IconButton, Box } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import TalentImageCarousel from './TalentImageCarousel';
import { IWorkProfile } from '../../types/global/helper';
import { isEmpty } from '../../utils/lodash';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import CustomButton from '../Button/CustomButton';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { BookmarkAddOutlined } from '@mui/icons-material';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import { ReusableDrawer } from '../ReusableDrawer/ReusableDrawer';
import { WorkProfile } from '../ScreenContainer/EsMyProfileContainer/WorkProfile';
import { MessageDialogue } from '../MessageDialogue/MessageDialogue';
import { TalentEditor } from '../TalentEditor/TalentEditor';
import RequireSignIn from '../HOC/RequireSignIn';
import { useDispatch } from 'react-redux';
import { deleteSaveTalent } from '../../redux/talent/actions';

interface TalentCardProps {
    talent: IWorkProfile;
    onZoomClick?: () => void;
    isMobile?: boolean;
}

const convertToMiles = (distance: number) => (distance / 1609.344).toFixed(2);

const TalentCard: React.FC<TalentCardProps> = ({ talent, onZoomClick, isMobile = false }) => {
    const images: string[] = [
        ...(talent.photos || []),
        ...(talent.profile_picture ? [talent.profile_picture] : []),
    ].filter(Boolean);
    const name = talent.stage_name || talent.created_by?.firstName + ' ' + talent.created_by?.lastName;
    const title = talent.title || '';
    const location = !isEmpty(talent.location) ? talent.location.address : '';
    const rating = talent.rating;
    const reviews = talent.number_of_reviews;
    const distance = talent.minDistance;
    const hasBookmarked = talent.has_user_saved;
    const { themeStyle, device } = useThemeContext();
    const [open, setOpen] = useState(false);
    const [messageDialogueOpen, setMessageDialogueOpen] = useState(false);
    const [saveTalentDialogue, setSaveDialogue] = useState(false);
    const dispatch = useDispatch();

    const handleSave = () => {
        if (hasBookmarked && talent.user_saved_object) {
            dispatch(
                deleteSaveTalent({
                    payload: talent,
                    saveId: talent.user_saved_object[0]._id,
                }),
            );
        } else {
            setSaveDialogue(!saveTalentDialogue);
        }
    };

    return (
        <Card
            sx={{
                borderRadius: 4.2,
                boxShadow: 2,
                background: 'none',
                p: 0,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                maxWidth: '100%',
                position: 'relative',
                bgcolor: theme[themeStyle].secondaryBg,
            }}>
            {talent.featured && (
                <Typography
                    sx={{
                        bgcolor: '#dea4a4',
                        fontSize: isMobile ? '6px' : '8px',
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        p: isMobile ? 0.5 : 1,
                        borderRadius: 0.1,
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        zIndex: 10,
                        color: theme[themeStyle].true_white,
                    }}>
                    FEATURED
                </Typography>
            )}
            {/* Message Dialogue */}
            <MessageDialogue
                open={messageDialogueOpen}
                onClose={() => setMessageDialogueOpen(false)}
                recipient={talent.user}
            />
            <ReusableDrawer
                open={open}
                onClose={() => {
                    setOpen(!open);
                }}
                content={
                    <WorkProfile
                        isEditMode={false}
                        workProfile={[talent]}
                        isPreviewMode
                        external
                        searchFlow
                        onMessageApplication={() => setMessageDialogueOpen(true)}
                    />
                }
                anchor={'right'}
            />
            <TalentImageCarousel
                images={images}
                name={name}
                onZoomClick={onZoomClick}
                onClick={() => setOpen(!open)}
                isMobile={isMobile}
            />

            <TalentEditor
                open={saveTalentDialogue}
                onClose={() => setSaveDialogue(!saveTalentDialogue)}
                profile={talent}
                user={talent.user}
            />

            {/* Content */}
            <Box sx={{ p: isMobile ? 1.5 : 2, pb: 1, bgcolor: theme[themeStyle].secondaryBg }}>
                <Typography
                    fontWeight={700}
                    noWrap
                    sx={{
                        fontSize: isMobile ? 14 : 16,
                        color: theme[themeStyle].tag_white,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        textTransform: 'capitalize',
                    }}
                    title={name}>
                    {name}
                </Typography>
                <Typography
                    fontWeight={500}
                    noWrap
                    sx={{
                        fontSize: isMobile ? 12 : 14,
                        color: theme[themeStyle].tag_white,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        textTransform: 'capitalize',
                    }}
                    title={title}>
                    {title}
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        mb: 0.3,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        color: theme[themeStyle].tag_white,
                        fontSize: isMobile ? 10 : 12,
                    }}>
                    {location}
                </Typography>
                {!!distance && (
                    <Typography
                        variant="body2"
                        sx={{
                            mb: 0.5,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            color: theme[themeStyle].darkGray,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: isMobile ? 7 : 8,
                        }}>
                        Approx. {convertToMiles(distance)} miles from you.
                    </Typography>
                )}
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                    <StarIcon sx={{ color: theme[themeStyle].highlight, fontSize: isMobile ? 14 : 18 }} />
                    <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{
                            color: theme[themeStyle].tag_white,
                            fontSize: isMobile ? 12 : 14,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        }}>
                        {reviews && reviews > 0 ? rating : 'No reviews yet'}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: theme[themeStyle].tag_white,
                            fontSize: isMobile ? 11 : 14,
                        }}>
                        {reviews ? `(${reviews})` : ''}
                    </Typography>
                </Stack>
            </Box>
            {/* Actions */}
            <RequireSignIn
                promptText={device.isMobile ? 'Sign In' : 'Sign In to continue'}
                sx={{ mx: 0.5, my: 0.5 }}
                textSx={{ fontSize: { xs: '10px', sm: '12px' } }}>
                <Stack
                    direction="row"
                    spacing={isMobile ? 1 : 2}
                    sx={{
                        p: isMobile ? 1.5 : 2,
                        pt: 0,
                        justifyContent: 'flex-end',
                        bgcolor: theme[themeStyle].secondaryBg,
                    }}>
                    <IconButton
                        color="primary"
                        onClick={handleSave}
                        sx={{
                            border: `1.5px solid ${theme[themeStyle].highlight}`,
                            borderRadius: 2,
                            width: isMobile ? 28 : 36,
                            height: isMobile ? 28 : 36,
                        }}>
                        {hasBookmarked ? (
                            <BookmarkIcon sx={{ color: theme[themeStyle].highlight, fontSize: isMobile ? 16 : 20 }} />
                        ) : (
                            <BookmarkAddOutlined
                                sx={{ color: theme[themeStyle].highlight, fontSize: isMobile ? 16 : 20 }}
                            />
                        )}
                    </IconButton>

                    <CustomButton
                        textStyle={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: isMobile ? 11 : 14,
                        }}
                        customStyle={{
                            borderRadius: '5px',
                            minWidth: isMobile ? 60 : 80,
                            height: isMobile ? 28 : 36,
                        }}
                        label="Contact"
                        onClick={() => setMessageDialogueOpen(true)}
                        withIcon
                        beginningIcon={
                            <PersonAddAltIcon
                                sx={{
                                    color: theme[themeStyle].black,
                                    mr: 0.5,
                                    fontSize: isMobile ? '12px' : '16px',
                                }}
                            />
                        }
                    />
                </Stack>
            </RequireSignIn>
        </Card>
    );
};

export default TalentCard;
